import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Activity, Flame, TrendingUp, DollarSign, Clock, ChevronRight, AlertTriangle, MapPin, ShieldAlert, History } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { fmtCurrencyFull } from "@/lib/format";
import PipelineMap from "@/components/maps/PipelineMap";

const TIER_BG = {
  hot: "bg-red-500/15 border-red-500/40 text-red-300",
  strong: "bg-orange-500/15 border-orange-500/40 text-orange-300",
  qualified: "bg-emerald-500/15 border-emerald-500/40 text-emerald-300",
  watchlist: "bg-slate-500/15 border-slate-500/40 text-slate-300",
  low: "bg-slate-700/30 border-slate-600/30 text-slate-400",
};


function StatCard({ icon: Icon, label, value, sub, testid }) {
  return (
    <div className="ae-card p-5" data-testid={testid}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-slate-500">
        <Icon className="h-3 w-3" /> {label}
      </div>
      <div className="font-heading text-3xl tracking-tight mt-2 text-slate-100">
        {value}
      </div>
      {sub && <div className="text-[11px] text-slate-500 mt-1">{sub}</div>}
    </div>
  );
}


function OppCard({ o }) {
  const tier = o.cf_score_tier || {};
  const tierStyle = TIER_BG[tier.tier] || TIER_BG.low;
  return (
    <Link to={`/app/opportunities/${o.oid}`}
          className="block ae-card p-3 hover:border-emerald-500/40 transition-colors"
          data-testid={`pipe-card-${o.oid}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-emerald-400 capitalize">{o.asset_type}</div>
          <div className="text-sm font-medium text-slate-100 truncate mt-0.5">{o.title}</div>
          <div className="text-[11px] text-slate-500 truncate">{o.city}, {o.state}</div>
        </div>
        {typeof o.cf_readiness_score === "number" && (
          <div className={`shrink-0 text-[10px] font-mono uppercase tracking-wider border rounded-sm px-1.5 py-0.5 ${tierStyle}`}>
            {o.cf_readiness_score}
          </div>
        )}
      </div>
      <div className="flex items-baseline justify-between mt-2 pt-2 border-t border-white/5">
        <div className="font-mono text-xs text-emerald-400 tabular-nums">
          {fmtCurrencyFull(o.asking_price)}
        </div>
        {o.cf_score_rising && (
          <span className="text-[9px] uppercase tracking-wider text-orange-300 inline-flex items-center gap-0.5">
            <TrendingUp className="h-2.5 w-2.5" /> Rising
          </span>
        )}
      </div>
      {o.motivation_flag && (
        <div className="mt-1.5 text-[9px] uppercase tracking-wider font-mono text-amber-300 truncate">
          {o.motivation_flag}
        </div>
      )}
      {/* Iter 60 Block 3 ,  passive badges. Informational; never identifies subscribers. */}
      {o.critical_elsewhere_stage && (
        <div
          className="mt-1.5 inline-flex items-center gap-1 text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm border border-amber-500/40 bg-amber-500/10 text-amber-300"
          data-testid="kanban-critical-elsewhere-badge"
          title={o.aggregate_label || "Another subscriber has reached a critical stage on this opportunity."}
        >
          <ShieldAlert className="h-2.5 w-2.5" />
          Advanced stage elsewhere
        </div>
      )}
      {!o.critical_elsewhere_stage && o.any_previously_pursued && (
        <div
          className="mt-1.5 inline-flex items-center gap-1 text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm border border-slate-500/40 bg-slate-500/10 text-slate-300"
          data-testid="kanban-previously-pursued-badge"
          title="A previous subscriber pursued this opportunity and marked it dead. It's back in the open pool."
        >
          <History className="h-2.5 w-2.5" />
          Previously pursued
        </div>
      )}
    </Link>
  );
}


export default function PipelineV2() {
  const [stages, setStages] = useState([]);
  const [stats, setStats] = useState(null);
  const [watchlist, setWatchlist] = useState([]);
  const [view, setView] = useState("pipeline");
  const [legend, setLegend] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mapData, setMapData] = useState(null);
  const [mapLoading, setMapLoading] = useState(false);

  const load = async () => {
    try {
      const [p, s, w, l] = await Promise.all([
        api.get("/pipeline-v2"),
        api.get("/pipeline-v2/stats"),
        api.get("/pipeline-v2/watchlist"),
        api.get("/pipeline-v2/legend"),
      ]);
      setStages(p.data.stages || []);
      setStats(s.data);
      setWatchlist(w.data.watchlist || []);
      setLegend(l.data);
    } catch (e) {
      toast.error("Failed to load pipeline");
    } finally {
      setLoading(false);
    }
  };

  const loadMap = async () => {
    if (mapData) return;
    setMapLoading(true);
    try {
      const r = await api.get("/pipeline-v2/map");
      setMapData(r.data);
    } catch {
      toast.error("Failed to load map view");
    } finally {
      setMapLoading(false);
    }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => { if (view === "map") loadMap(); }, [view]);

  if (loading) return <div className="text-slate-500">Loading...</div>;

  return (
    <div className="space-y-6" data-testid="pipeline-v2-page">
      <header className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-3xl tracking-tight">Acquisition Pipeline</h1>
          <p className="text-sm text-slate-500 mt-1">
            10-stage automated workflow from Discovered through Closed. Score &gt;= 55 surfaces here.
            Lower-scored opportunities live in the Discovery Queue.
          </p>
        </div>
        <div className="flex gap-2" data-testid="pipeline-v2-view-toggle">
          <button
            onClick={() => setView("pipeline")}
            data-testid="pipeline-v2-tab-pipeline"
            className={`px-3 py-1.5 text-xs uppercase tracking-wider rounded-sm border transition-colors ${
              view === "pipeline" ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/40"
                                  : "bg-white/0 text-slate-400 border-white/10 hover:border-white/20"
            }`}
          >Pipeline</button>
          <button
            onClick={() => setView("watchlist")}
            data-testid="pipeline-v2-tab-watchlist"
            className={`px-3 py-1.5 text-xs uppercase tracking-wider rounded-sm border transition-colors ${
              view === "watchlist" ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/40"
                                   : "bg-white/0 text-slate-400 border-white/10 hover:border-white/20"
            }`}
          >Discovery Queue ({watchlist.length})</button>
          <button
            onClick={() => setView("map")}
            data-testid="pipeline-v2-tab-map"
            className={`px-3 py-1.5 text-xs uppercase tracking-wider rounded-sm border transition-colors inline-flex items-center gap-1 ${
              view === "map" ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/40"
                             : "bg-white/0 text-slate-400 border-white/10 hover:border-white/20"
            }`}
          ><MapPin className="h-3 w-3" /> Map{mapData ? ` (${mapData.count})` : ""}</button>
        </div>
      </header>

      {/* Stat cards */}
      {stats && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="pipeline-v2-totals">
            <StatCard icon={Activity} label="Total opportunities" value={stats.total_opportunities ?? ", "}
                      sub="All discovered leads" testid="stat-total-opps" />
            <StatCard icon={Activity} label="In active pipeline" value={stats.in_pipeline ?? ", "}
                      sub={`Score ≥ 55 · ${stats.in_active_pipeline ?? 0} total un-archived`} testid="stat-in-pipeline" />
            <StatCard icon={Activity} label="In discovery queue" value={stats.in_watchlist ?? ", "}
                      sub="Score 40-54" testid="stat-in-watchlist" />
            <StatCard icon={Activity} label="In backlog" value={stats.in_backlog ?? ", "}
                      sub="Score < 40 · auto-archived" testid="stat-in-backlog" />
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="pipeline-v2-stats">
            <StatCard icon={Flame} label="Hot leads, 7d" value={stats.hot_leads_7d}
                      sub="Opportunities scoring 85+" testid="stat-hot-leads" />
            <StatCard icon={DollarSign} label="Pipeline value" value={fmtCurrencyFull(stats.deal_value_in_pipeline)}
                      sub="Engaged, Offer, LOI, DD, Signed" testid="stat-pipeline-value" />
            <StatCard icon={Activity} label="Response rate, 30d" value={`${stats.response_rate_30d}%`}
                      sub={`${stats.replied_30d}/${stats.sent_30d} replied`} testid="stat-response-rate" />
            <StatCard icon={Clock} label="Velocity" value={stats.velocity_days ? `${stats.velocity_days}d` : "N/A"}
                      sub="Avg Discovered to Signed" testid="stat-velocity" />
          </div>
        </>
      )}

      {view === "pipeline" ? (
        <div className="overflow-x-auto pb-4" data-testid="pipeline-v2-kanban">
          <div className="flex gap-3 min-w-max">
            {stages.map((stage) => {
              const stageMeta = legend?.stages?.find((s) => s.id === stage.id);
              return (
                <div key={stage.id}
                     className="w-72 shrink-0 ae-card p-3 bg-white/2"
                     data-testid={`pipeline-v2-column-${stage.id}`}>
                  <div className="flex items-center justify-between pb-2 border-b border-white/8">
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-slate-500 flex items-center gap-1.5">
                        {stage.label}
                        {stageMeta?.gated && (
                          <span className="text-[9px] bg-amber-500/15 text-amber-300 border border-amber-500/30 rounded-sm px-1 py-px font-mono"
                                title="Paid tier required to advance into this stage">PRO</span>
                        )}
                      </div>
                      <div className="font-mono text-xl text-slate-200 mt-0.5 tabular-nums">{stage.items.length}</div>
                    </div>
                    {stageMeta?.manual && (
                      <span className="text-[9px] uppercase tracking-wider text-slate-500" title="Manual stage advance">M</span>
                    )}
                  </div>
                  <div className="mt-3 space-y-2 max-h-[500px] overflow-y-auto">
                    {stage.items.length === 0 ? (
                      <div className="text-[11px] text-slate-600 italic py-3 text-center">No opportunities</div>
                    ) : (
                      stage.items.map((o) => <OppCard key={o.oid} o={o} />)
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : view === "watchlist" ? (
        <div className="space-y-3" data-testid="pipeline-v2-watchlist">
          {watchlist.length === 0 ? (
            <div className="ae-card p-8 text-center text-slate-500">
              <AlertTriangle className="h-6 w-6 mx-auto mb-3 text-slate-600" />
              <div className="text-sm">No opportunities currently in the Discovery Queue (score 40-54).</div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {watchlist.map((o) => <OppCard key={o.oid} o={o} />)}
            </div>
          )}
        </div>
      ) : (
        // view === "map"
        mapLoading && !mapData ? (
          <div className="ae-card p-12 text-center text-slate-500 text-sm" data-testid="pipeline-map-loading">
            Loading map…
          </div>
        ) : mapData && mapData.count === 0 ? (
          <div className="ae-card p-8 text-center text-slate-500" data-testid="pipeline-map-empty">
            <MapPin className="h-6 w-6 mx-auto mb-3 text-slate-600" />
            <div className="text-sm">No opportunities have geocoded coordinates yet.</div>
            <div className="text-xs mt-1">
              {mapData.missing_coords > 0 && `${mapData.missing_coords} opportunities are missing lat/lng. `}
              Add an address on the Intake page and the Geocoder will populate coords automatically.
            </div>
          </div>
        ) : (
          <PipelineMap points={mapData?.points || []} missing={mapData?.missing_coords || 0} />
        )
      )}
    </div>
  );
}
